import { useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { agentStore } from '@/stores/AgentStore'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import { AgentRole, TeamMemberCreate } from '@/types'
import {
  PlusIcon,
  TrashIcon,
  UserIcon,
  ArrowsUpDownIcon,
} from '@heroicons/react/24/outline'

interface TeamMemberSelectorProps {
  members: TeamMemberCreate[]
  onChange: (members: TeamMemberCreate[]) => void
  disabled?: boolean
}

const roleOptions = [
  { value: AgentRole.COORDINATOR, label: 'Coordinator', description: 'Manages workflow' },
  { value: AgentRole.RESEARCHER, label: 'Researcher', description: 'Gathers information' },
  { value: AgentRole.ANALYST, label: 'Analyst', description: 'Analyzes data' },
  { value: AgentRole.WRITER, label: 'Writer', description: 'Creates content/code' },
  { value: AgentRole.REVIEWER, label: 'Reviewer', description: 'Reviews output' },
  { value: AgentRole.VALIDATOR, label: 'Validator', description: 'Validates results' },
  { value: AgentRole.TESTER, label: 'Tester', description: 'Tests functionality' },
  { value: AgentRole.DEBUGGER, label: 'Debugger', description: 'Fixes issues' },
  { value: AgentRole.OPTIMIZER, label: 'Optimizer', description: 'Optimizes performance' },
  { value: AgentRole.SPECIALIST, label: 'Specialist', description: 'Domain expert' },
  { value: AgentRole.CUSTOM, label: 'Custom', description: 'Custom role' },
]

const getRoleBadgeVariant = (role: AgentRole) => {
  const variants: Record<AgentRole, 'primary' | 'success' | 'warning' | 'error' | 'gray'> = {
    [AgentRole.COORDINATOR]: 'primary',
    [AgentRole.RESEARCHER]: 'success',
    [AgentRole.ANALYST]: 'warning',
    [AgentRole.WRITER]: 'primary',
    [AgentRole.REVIEWER]: 'warning',
    [AgentRole.VALIDATOR]: 'error',
    [AgentRole.TESTER]: 'success',
    [AgentRole.DEBUGGER]: 'error',
    [AgentRole.OPTIMIZER]: 'success',
    [AgentRole.SPECIALIST]: 'primary',
    [AgentRole.CUSTOM]: 'gray',
  }
  return variants[role]
}

const TeamMemberSelector = observer(({ members, onChange, disabled = false }: TeamMemberSelectorProps) => {
  const [showAddForm, setShowAddForm] = useState(false)
  const [newMember, setNewMember] = useState<TeamMemberCreate>({
    agent_id: '',
    role: AgentRole.WRITER,
    role_description: '',
    execution_order: members.length,
    is_required: true,
    can_delegate: false,
    feedback_sources: [],
    feedback_targets: [],
    configuration: {},
  })

  useEffect(() => {
    agentStore.fetchAgents()
  }, [])

  const handleAddMember = () => {
    if (!newMember.agent_id) {
      return
    }

    onChange([...members, newMember])
    setNewMember({
      agent_id: '',
      role: AgentRole.WRITER,
      role_description: '',
      execution_order: members.length + 1,
      is_required: true,
      can_delegate: false,
      feedback_sources: [],
      feedback_targets: [],
      configuration: {},
    })
    setShowAddForm(false)
  }

  const handleRemoveMember = (index: number) => {
    const updated = members.filter((_, i) => i !== index)
    // Reorder remaining members
    const reordered = updated.map((m, i) => ({ ...m, execution_order: i }))
    onChange(reordered)
  }

  const handleMoveUp = (index: number) => {
    if (index === 0) return
    const updated = [...members]
    ;[updated[index - 1], updated[index]] = [updated[index], updated[index - 1]]
    // Update execution orders
    const reordered = updated.map((m, i) => ({ ...m, execution_order: i }))
    onChange(reordered)
  }

  const handleMoveDown = (index: number) => {
    if (index === members.length - 1) return
    const updated = [...members]
    ;[updated[index], updated[index + 1]] = [updated[index + 1], updated[index]]
    // Update execution orders
    const reordered = updated.map((m, i) => ({ ...m, execution_order: i }))
    onChange(reordered)
  }

  const getAgentName = (agentId: string) => {
    const agent = agentStore.agents.find((a) => a.id === agentId)
    return agent?.name || agentId
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-900 mb-1">
          Team Members
        </label>
        <p className="text-sm text-gray-600">
          Add agents to this team and assign their roles
        </p>
      </div>

      {/* Member List */}
      {members.length > 0 && (
        <div className="space-y-2">
          {members.map((member, index) => (
            <div
              key={index}
              className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg"
            >
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span className="font-mono">{index + 1}</span>
                <ArrowsUpDownIcon className="w-4 h-4" />
              </div>

              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <UserIcon className="w-4 h-4 text-gray-400" />
                  <span className="font-medium text-gray-900">{getAgentName(member.agent_id)}</span>
                  <Badge variant={getRoleBadgeVariant(member.role)} size="sm">
                    {member.role}
                  </Badge>
                  {member.is_required && (
                    <Badge variant="error" size="sm">
                      Required
                    </Badge>
                  )}
                </div>
                {member.role_description && (
                  <p className="text-sm text-gray-600 mt-1">{member.role_description}</p>
                )}
              </div>

              <div className="flex items-center gap-1">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleMoveUp(index)}
                  disabled={disabled || index === 0}
                  title="Move up"
                >
                  ↑
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleMoveDown(index)}
                  disabled={disabled || index === members.length - 1}
                  title="Move down"
                >
                  ↓
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleRemoveMember(index)}
                  disabled={disabled}
                  title="Remove"
                >
                  <TrashIcon className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Member Form */}
      {showAddForm ? (
        <div className="p-4 bg-white border-2 border-primary-300 rounded-lg space-y-4">
          <h4 className="font-medium text-gray-900">Add Team Member</h4>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Agent <span className="text-red-500">*</span>
            </label>
            <select
              value={newMember.agent_id}
              onChange={(e) => setNewMember({ ...newMember, agent_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
            >
              <option value="">-- Select an agent --</option>
              {agentStore.agents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name} ({agent.framework})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role <span className="text-red-500">*</span>
            </label>
            <select
              value={newMember.role}
              onChange={(e) => setNewMember({ ...newMember, role: e.target.value as AgentRole })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
            >
              {roleOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label} - {option.description}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role Description (Optional)
            </label>
            <input
              type="text"
              value={newMember.role_description}
              onChange={(e) => setNewMember({ ...newMember, role_description: e.target.value })}
              placeholder="e.g., Validates code quality and security"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="is-required"
              checked={newMember.is_required}
              onChange={(e) => setNewMember({ ...newMember, is_required: e.target.checked })}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <label htmlFor="is-required" className="text-sm text-gray-700">
              Required member (team cannot execute without this agent)
            </label>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="primary" onClick={handleAddMember} disabled={!newMember.agent_id}>
              <PlusIcon className="w-4 h-4 mr-1" />
              Add Member
            </Button>
            <Button variant="secondary" onClick={() => setShowAddForm(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="secondary"
          onClick={() => setShowAddForm(true)}
          disabled={disabled}
          className="w-full"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Add Team Member
        </Button>
      )}

      {members.length === 0 && !showAddForm && (
        <div className="text-center py-8 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg">
          <UserIcon className="w-12 h-12 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-600">No team members yet</p>
          <p className="text-xs text-gray-500 mt-1">Click "Add Team Member" to get started</p>
        </div>
      )}
    </div>
  )
})

export default TeamMemberSelector
